/* eslint-disable no-unneeded-ternary */
/* eslint-disable arrow-body-style */
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
// @mui
import {
    Box, Typography, MenuItem,
    Button, Divider, Stack
} from '@mui/material';
import { useSelector } from 'react-redux';
import { alpha, useTheme } from '@mui/material/styles';
import { updateSettingsMenuActive } from 'src/redux/slices/mergeTags';
import { dispatch } from 'src/redux/store';
import { MENU_OPTIONS } from '../constants/SettingsMenuOptions';
import { Iconify } from '.';
import { useGetCurrentUser } from '../hooks';
import { getMenuOptions } from '../utils/settingUtils';


// ----------------------------------------------------------------------------------------------------------
const SettingsMenu = ({ selected }) => {
    const theme = useTheme();
    const { isSuperUser } = useGetCurrentUser();

    const {settingsMenuActive} = useSelector((state) => state.mergeTags);

    // Group menu options by their `group` field, preserving order
    const groupedOptions = useMemo(() => {
        const filtered = getMenuOptions(MENU_OPTIONS, isSuperUser) || [];
        const groups = [];
        const seen = new Set();
        filtered.forEach((option) => {
            const groupName = option.group || 'Other';
            if (!seen.has(groupName)) {
                seen.add(groupName);
                groups.push({ name: groupName, items: [] });
            }
            groups.find((g) => g.name === groupName).items.push(option);
        });
        return groups;
    }, [isSuperUser]);

    return (
        <Box sx={{ '@media(max-width: 1200px)': { display: 'none' }, ...(settingsMenuActive && { p: 2, pt: 3 }) }}>
            <Button
                sx={{
                    mb: 1.5,
                    ml: 0,
                    minWidth: '200px',
                    width: '100%',
                    bgcolor: '#00A76F',
                    '&:hover': { bgcolor: '#007B55' },
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: '8px',
                }}
                variant='contained'
                onClick={() => dispatch(updateSettingsMenuActive(!settingsMenuActive))}
                startIcon={<Iconify icon={'solar:arrow-left-bold'} />}
            >
                Back to Dashboard
            </Button>
            <Box sx={settingsMenuActive && { mt: 1 }}>
                <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: theme.palette.text.primary, mb: 1.5, pl: 0.5 }}>
                    Settings
                </Typography>
                <Stack spacing={0}>
                    {groupedOptions.map((group, groupIndex) => (
                        <Box key={group.name}>
                            {groupIndex > 0 && <Divider sx={{ my: 1, mx: 0.5 }} />}
                            <Typography
                                sx={{
                                    px: 1,
                                    pt: groupIndex === 0 ? 0 : 0.5,
                                    pb: 0.5,
                                    fontSize: '0.6875rem',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.08em',
                                    color: theme.palette.text.disabled,
                                }}
                            >
                                {group.name}
                            </Typography>
                            {group.items.map((option) => {
                                const isSelected = selected === option.value;
                                return (
                                    <MenuItem
                                        key={option.value}
                                        selected={isSelected}
                                        to={option.linkTo}
                                        component={RouterLink}
                                        sx={{
                                            px: 1,
                                            py: 0.75,
                                            mb: '2px',
                                            minHeight: 'auto',
                                            borderRadius: '8px',
                                            '&:hover': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                            },
                                            '&:hover .settings-sidebar-icon': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                                                '& svg': { color: theme.palette.primary.main },
                                            },
                                            '&.Mui-selected': {
                                                bgcolor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
                                            },
                                            '&.Mui-selected .settings-sidebar-icon': {
                                                bgcolor: alpha(theme.palette.primary.main, 0.16),
                                                '& svg': { color: theme.palette.primary.main },
                                            },
                                            '&.Mui-selected .settings-sidebar-label': {
                                                color: theme.palette.primary.main,
                                            },
                                        }}
                                    >
                                        <Box
                                            className="settings-sidebar-icon"
                                            sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: alpha(theme.palette.grey[500], 0.08),
                                                mr: 1.5,
                                                flexShrink: 0,
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <Iconify
                                                icon={option.icon}
                                                width={18}
                                                sx={{ color: theme.palette.text.secondary, transition: 'color 0.2s' }}
                                            />
                                        </Box>
                                        <Typography
                                            className="settings-sidebar-label"
                                            sx={{
                                                fontSize: '0.8125rem',
                                                fontWeight: 500,
                                                color: theme.palette.text.secondary,
                                                lineHeight: 1.4,
                                                transition: 'color 0.2s',
                                            }}
                                        >
                                            {option.label}
                                        </Typography>
                                    </MenuItem>
                                );
                            })}
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
};

SettingsMenu.propTypes = {
    selected: PropTypes.string,
};

export default SettingsMenu;
